/**
 * Registry des portées — quelles capacités le copilote a, et où.
 *
 * @remarks
 * **Du contrat, pas de la data.** Ce qui existe (quelles capacités, sur quelle
 * cible, avec quels hydrateurs) est validé par le compilateur et partagé
 * front/back. Ce qui est *dit au modèle* — le prompt — reste de la data, dans
 * `ms-llm-profile` (fragment → template → preset), éditable sans déploiement.
 *
 * Côté back, ce fichier a un jumeau dans `shared-types` : le front n'énumère
 * ici que ce dont l'UI a besoin (libellés, amorces de message). `slot` est le
 * slot `ms-llm-profile` que l'orchestrateur résoudra — dédié au copilote, et
 * surtout pas les presets `chat/*` déjà seedés, qui visent un chat apprenant.
 */

import type { CopilotCapability } from '@intra-alt/shared-types';
import { CONTEXTS, type Context } from '@/app/registry';
import type { CopilotTargetKind } from './copilot-scope';

/**
 * Le catalogue fermé vient de shared-types : le back valide `capability` contre
 * la même liste. Seuls les libellés et les amorces de message restent ici.
 */
export type CopilotCapabilityId = CopilotCapability;

export interface CopilotCapabilityDef {
  /** Libellé de la puce affichée dans le panneau. */
  readonly label: string;
  /** Message injecté dans le fil quand on clique la puce. */
  readonly prompt: string;
}

/**
 * Le catalogue. Une capacité = une intention nommée, pas un prompt libre :
 * c'est ce qui distingue ce copilote d'une zone de chat posée dans un panneau.
 */
export const COPILOT_CAPABILITIES: Record<CopilotCapabilityId, CopilotCapabilityDef> = {
  draft_template: {
    label: 'Rédiger le passage',
    prompt: 'Rédige le contenu de ce nœud dans le style demandé, deux paragraphes maximum.',
  },
  translate_locale: {
    label: 'Traduire en EN',
    prompt: 'Traduis le contenu FR en anglais en conservant les variables {{…}} intactes.',
  },
  suggest_variables: {
    label: 'Vérifier les variables',
    prompt: 'Vérifie que chaque {{variable}} du template a une valeur par défaut, et l’inverse.',
  },
  review_pedagogy: {
    label: 'Relire la pédagogie',
    prompt: 'Relis ce nœud : cohérence entre le mode, le style annoncé et le contenu réel.',
  },
  audit_transitions: {
    label: 'Auditer les transitions',
    prompt: 'Cherche les culs-de-sac et les intents sans transition sortante.',
  },
  audit_coverage: {
    label: 'Couverture par compétence',
    prompt: 'Compare le nombre de nœuds par compétence au seuil visé et signale les trous.',
  },
  check_prerequisites: {
    label: 'Vérifier les prérequis',
    prompt: 'Vérifie les prérequis déclarés du concept : manquants, circulaires, superflus.',
  },
  review_backlog: {
    label: 'Trier le backlog',
    prompt: 'Que faut-il relire en priorité dans cette liste, et pourquoi ?',
  },
  draft_rule_description: {
    label: 'Rédiger la règle',
    prompt:
      'Rédige la description de cette règle en gardant à l’esprit qu’elle sera exécutée telle quelle par l’agent d’évaluation.',
  },
  propose_checkboxes: {
    label: 'Proposer des checkboxes',
    prompt: 'Propose les critères vérifiables qui découpent cette règle, avec leurs points.',
  },
  draft_criterion: {
    label: 'Reformuler le critère',
    prompt: 'Reformule ce critère pour qu’il soit vérifiable sans interprétation.',
  },
  assert_decidable: {
    label: 'Est-ce décidable ?',
    prompt:
      'Ce critère est-il décidable par l’agent avec search_code, find_tests et analyze_ast sur un repo candidat ?',
  },
  suggest_points: {
    label: 'Calibrer les points',
    prompt: 'Propose une répartition de points cohérente avec les autres critères de la règle.',
  },
  draft_test_description: {
    label: 'Rédiger la description',
    prompt: 'Rédige le synopsis et la description de ce test à partir de ses évaluations.',
  },
  check_consistency: {
    label: 'Vérifier la cohérence',
    prompt: 'La durée annoncée et la description correspondent-elles aux évaluations rattachées ?',
  },
  draft_evaluation_synopsis: {
    label: 'Rédiger le synopsis',
    prompt: 'Rédige un synopsis court et une description pour cette évaluation.',
  },
  draft_default_prompt: {
    label: 'Rédiger le prompt',
    prompt: 'Propose un defaultPrompt pour cette évaluation, aligné sur ses steps.',
  },
  draft_fragment: {
    label: 'Rédiger le fragment',
    prompt: 'Aide-moi à écrire ce fragment de prompt sans dupliquer ce que porte le template.',
  },
  check_token_budget: {
    label: 'Budget tokens',
    prompt: 'Ce prompt tient-il dans le budget, et qu’est-ce qui pèse le plus ?',
  },
  draft_variant_brief: {
    label: 'Rédiger le brief',
    prompt: 'Rédige le brief candidat de cette variante à partir de ses steps.',
  },
  write_variant_document: {
    label: 'Écrire les consignes',
    prompt: 'Rédige les consignes candidat et ajoute-les comme document de la variante.',
  },
  draft_skill_description: {
    label: 'Rédiger la description',
    prompt: 'Rédige la description de cette compétence : ce qu’elle recouvre, en deux phrases.',
  },
  draft_skill_level: {
    label: 'Décrire le palier',
    prompt: 'Décris ce que le candidat sait faire à ce palier, en observable, pas en intention.',
  },
  draft_skill_group_description: {
    label: 'Rédiger la description',
    prompt: 'Rédige la description de ce groupe à partir des compétences qu’il rassemble.',
  },
  check_group_coherence: {
    label: 'Vérifier la cohérence',
    prompt:
      'Les compétences liées forment-elles un ensemble cohérent ? Signale les intruses et les manques.',
  },
  draft_position_description: {
    label: 'Rédiger la description',
    prompt: 'Rédige la description de ce poste à partir des groupes de compétences qu’il exige.',
  },
  check_position_groups: {
    label: 'Vérifier les groupes',
    prompt: 'Les groupes liés couvrent-ils ce que la description du poste promet ?',
  },
  draft_step_description: {
    label: 'Rédiger la description',
    prompt:
      'Rédige la description de cette étape : contexte et objectif, pour l’agent d’évaluation.',
  },
};

/** Slots `ms-llm-profile` propres au copilote (jamais les presets `chat/*`). */
export type CopilotSlot = 'COPILOT_AUTHORING' | 'COPILOT_SCHEMA' | 'COPILOT_REVIEW';

export interface CopilotScopeDef {
  /**
   * Cible du contexte. `param` est le paramètre d'URL qui porte sa clé — c'est
   * ce qui rend la portée dérivable, jamais saisie à la main.
   */
  readonly target?: { readonly kind: CopilotTargetKind; readonly param: string };
  /** Ce que l'orchestrateur devra hydrater côté back avant d'appeler le worker. */
  readonly hydrate: readonly string[];
  readonly capabilities: readonly CopilotCapabilityId[];
  readonly slot: CopilotSlot;
}

export const COPILOT_SCOPES: Partial<Record<Context, CopilotScopeDef>> = {
  [CONTEXTS.ADMIN_SKILL_UP_NODES]: {
    hydrate: ['node_list'],
    capabilities: ['review_backlog'],
    slot: 'COPILOT_REVIEW',
  },
  [CONTEXTS.ADMIN_SKILL_UP_NODE_FORM]: {
    target: { kind: 'skill_up_node', param: 'nodeId' },
    hydrate: ['node', 'skill', 'sibling_nodes', 'style_guide'],
    capabilities: ['draft_template', 'translate_locale', 'suggest_variables', 'review_pedagogy'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_SKILL_UP_NODE_LINKS]: {
    target: { kind: 'skill_up_node', param: 'nodeId' },
    hydrate: ['node', 'transitions', 'concept_graph'],
    capabilities: ['audit_transitions'],
    slot: 'COPILOT_REVIEW',
  },
  [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_FORM]: {
    target: { kind: 'skill_up_concept', param: 'conceptId' },
    hydrate: ['concept', 'skills'],
    // Le formulaire de concept n'a pas de champ de description : seule la
    // vérification des prérequis a un sens ici.
    capabilities: ['check_prerequisites'],
    slot: 'COPILOT_REVIEW',
  },
  // ── Référentiel ────────────────────────────────────────────────────────
  [CONTEXTS.ADMIN_SKILL_FORM]: {
    target: { kind: 'skill', param: 'skillId' },
    hydrate: ['skill', 'levels', 'groups_using_it'],
    // `draft_skill_level` vise le panneau « Palier » (champ `levels.description`).
    capabilities: ['draft_skill_description', 'draft_skill_level'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_SKILL_GROUP_FORM]: {
    target: { kind: 'skill_group', param: 'skillGroupId' },
    hydrate: ['skill_group', 'skills'],
    capabilities: ['draft_skill_group_description', 'check_group_coherence'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_SKILL_GROUP_CREATE]: {
    hydrate: [],
    capabilities: ['draft_skill_group_description'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_POSITION_FORM]: {
    target: { kind: 'position', param: 'positionId' },
    hydrate: ['position', 'skill_groups'],
    capabilities: ['draft_position_description', 'check_position_groups'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_POSITION_CREATE]: {
    hydrate: [],
    capabilities: ['draft_position_description'],
    slot: 'COPILOT_AUTHORING',
  },
  // ── Bibliothèques ──────────────────────────────────────────────────────
  // Le formulaire d'étape est une page (`/admin/steps/new`, `…/:stepId/edit`) :
  // c'est lui qui porte la description à rédiger, plus la liste des étapes. La
  // portée reste au niveau du contexte, le champ ciblé vient du ✨.
  [CONTEXTS.ADMIN_STEP_FORM]: {
    hydrate: ['step_draft', 'sibling_steps'],
    capabilities: ['draft_step_description'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_SKILL_UP_CONCEPT_ROADMAP]: {
    target: { kind: 'skill_up_concept', param: 'conceptId' },
    hydrate: ['concept_graph', 'skill_node_counts', 'prerequisites'],
    capabilities: ['audit_coverage', 'audit_transitions', 'check_prerequisites'],
    slot: 'COPILOT_REVIEW',
  },
  [CONTEXTS.ADMIN_RULES_LIBRARY]: {
    hydrate: ['rule_draft', 'sibling_rules'],
    capabilities: ['draft_rule_description', 'propose_checkboxes'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_CHECKBOXES_LIBRARY]: {
    hydrate: ['checkbox_list'],
    capabilities: ['review_backlog'],
    slot: 'COPILOT_REVIEW',
  },
  [CONTEXTS.ADMIN_CHECKBOX_FORM]: {
    target: { kind: 'checkbox', param: 'checkboxId' },
    hydrate: ['checkbox', 'rule', 'sibling_checkboxes', 'agent_toolset'],
    capabilities: ['draft_criterion', 'assert_decidable', 'suggest_points'],
    slot: 'COPILOT_SCHEMA',
  },
  [CONTEXTS.ADMIN_TEST_FORM]: {
    target: { kind: 'test', param: 'testKey' },
    hydrate: ['test', 'evaluations'],
    capabilities: ['draft_test_description', 'check_consistency'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_EVALUATION_VARIANT_FORM]: {
    target: { kind: 'evaluation_variant', param: 'variantId' },
    hydrate: ['variant', 'steps', 'documents'],
    // `write_variant_document` écrit dans ms-graph : c'est le cas du schéma
    // d'architecture, et celui qui demande une autorisation explicite.
    capabilities: ['draft_variant_brief', 'write_variant_document'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_EVALUATION_FORM]: {
    target: { kind: 'evaluation', param: 'evaluationId' },
    hydrate: ['evaluation', 'variants', 'steps'],
    capabilities: ['draft_evaluation_synopsis', 'draft_default_prompt'],
    slot: 'COPILOT_AUTHORING',
  },
  // Le template a disparu (ADR SAAS-A-184 D1, SAAS-995) : un fragment est
  // maintenant référencé par des presets, pas des templates.
  [CONTEXTS.ADMIN_LLM_FRAGMENT_FORM]: {
    target: { kind: 'llm_fragment', param: 'key' },
    hydrate: ['fragment', 'presets_using_it'],
    capabilities: ['draft_fragment', 'check_token_budget'],
    slot: 'COPILOT_AUTHORING',
  },
  [CONTEXTS.ADMIN_LLM_PRESET_FORM]: {
    target: { kind: 'llm_preset', param: 'id' },
    hydrate: ['preset', 'fragments', 'resolved_prompt'],
    capabilities: ['check_token_budget'],
    slot: 'COPILOT_REVIEW',
  },
};

/** Les contextes où le copilote a quelque chose à proposer. */
export const COPILOT_CONTEXTS = Object.keys(COPILOT_SCOPES) as Context[];

export function getScopeDef(context: Context): CopilotScopeDef | undefined {
  return COPILOT_SCOPES[context];
}
